import { reactive } from 'vue';
import { CARD_LIMITS, generateCard, type CardModel, type CardUse } from '@vp/shared-types';
import { ulid } from '@vp/core';
import { profileLocal, profileScope } from './profiles/local';
import { setUserProfilePanels } from './profile-panels';
import { forgetCard } from './profile-looks';

/**
 * The person's own profile cards, made in the platform's card editor (`CardEditorView.vue`): the
 * design (`CardModel`) and the HTML the generator made of it. A key of the profile
 * (`vp-profile-user-cards`, rides along in `.vpprofile`, `sync` / `rev` for a future sync). On the
 * tray a card is `user:<id>`; it runs on the same card page as module cards, without scripts, and
 * shows the profile's counters without any permission — no third-party code takes part in it.
 */
export const USER_CARDS_KEY = 'vp-profile-user-cards';

/** The card the editor opens: an id, or null for a new one (set before going to the `cards` view). */
export const cardEditor = reactive({ id: null as string | null });

export interface UserCard {
  model: CardModel;
  html: string;
  uses: CardUse[];
  updatedAt: number;
}

interface Sheet {
  v: 1;
  sync: 'pending' | 'acked';
  rev: number;
  cards: Record<string, UserCard>;
}

function load(): Sheet {
  try {
    const raw = JSON.parse(profileLocal.getItem(USER_CARDS_KEY) ?? 'null') as Partial<Sheet> | null;
    const cards: Record<string, UserCard> = {};
    for (const [id, c] of Object.entries(raw?.cards ?? {})) if (c && typeof c.html === 'string' && c.model && Array.isArray(c.model.elements)) cards[id] = c;
    return { v: 1, sync: raw?.sync === 'pending' ? 'pending' : 'acked', rev: typeof raw?.rev === 'number' ? raw.rev : 0, cards };
  } catch {
    return { v: 1, sync: 'acked', rev: 0, cards: {} };
  }
}

const state = reactive({ sheet: load(), scope: profileScope() });

function sheet(): Sheet {
  if (state.scope !== profileScope()) {
    state.sheet = load();
    state.scope = profileScope();
    publish();
  }
  return state.sheet;
}

/** The library knows every card the person made. */
function publish(): void {
  setUserProfilePanels(
    Object.entries(state.sheet.cards).map(([id, c]) => ({
      id: `user:${id}`,
      origin: 'user' as const,
      card: id,
      title: { ru: c.model.title, en: c.model.title },
      size: { w: Math.min(760, Math.max(120, c.model.w)), h: Math.min(483, Math.max(84, c.model.h)) },
      look: c.model.look,
      uses: c.uses,
      html: c.html,
    })),
  );
}

function save(): void {
  const s = sheet();
  s.sync = 'pending';
  s.rev++;
  try {
    profileLocal.setItem(USER_CARDS_KEY, JSON.stringify(s));
  } catch {
    /* storage full or blocked: this visit still has it */
  }
  publish();
}

export function userCards(): [string, UserCard][] {
  return Object.entries(sheet().cards).sort((a, b) => b[1].updatedAt - a[1].updatedAt);
}

export function userCard(id: string): UserCard | undefined {
  return sheet().cards[id];
}

export class UserCardLimitError extends Error {}

/** Save a design (a new card when `id` is null); the id of the card. */
export function saveUserCard(id: string | null, model: CardModel): string {
  const s = sheet();
  if (!id && Object.keys(s.cards).length >= CARD_LIMITS.cards) throw new UserCardLimitError(`at most ${CARD_LIMITS.cards} cards`);
  const key = id && s.cards[id] ? id : 'c' + ulid().toLowerCase();
  const { html, uses } = generateCard(model);
  s.cards[key] = { model: JSON.parse(JSON.stringify(model)) as CardModel, html, uses, updatedAt: Date.now() };
  save();
  return key;
}

export function removeUserCard(id: string): void {
  const s = sheet();
  if (!s.cards[id]) return;
  delete s.cards[id];
  forgetCard(`user:${id}`);
  save();
}

/** At start (and after a profile switch, through `sheet()`): the library learns the cards. */
export function initUserCards(): void {
  sheet();
  publish();
}
